<template>
  <div class="dynamic-article-tabs">
    <header class="dynamic-article-tabs-header">
      <h3>{{ title }}</h3>
    </header>

    <dynamic-tabs-nav
      :label="navLabel"
      :links="links"
      :active-link="activeTag"
      @select="changeArticles"
      @show-all="showAll"
    />

    <ul class="dynamic-article-tabs-content-grid">
      <article-excerpt
        v-for="article in activeArticles"
        :id="article.id"
        :key="article.id"
        :title="article.title"
        :image-src="article.imageSrc"
        :meta="article.meta"
        :url="article.url"
        :excerpt="article.excerpt"
        :read-more-label="readMoreLabel"
        :image-placeholder="imagePlaceholder"
        :published-date="article.publishedDate"
        :can-show-published-date="canShowPublishedDate"
      />
    </ul>

    <footer>
      <dynamic-tabs-pagination
        v-if="activePageCount > 1"
        ref="pagination"
        :prev-label="prevLabel"
        :next-label="nextLabel"
        :page-count="activePageCount"
        :current-page="currentPage"
        @change-page="changePage"
      />
    </footer>
  </div>
</template>

<script>
import DynamicTabsNav from './DynamicTabsNav.vue';
import DynamicTabsPagination from './DynamicTabsPagination.vue';
import ArticleExcerpt from './ArticleExcerpt.vue';

export default {
  components: {
    ArticleExcerpt,
    DynamicTabsNav,
    DynamicTabsPagination
  },
  props: {
    title: {
      type: String
    },
    navLabel: {
      type: String
    },
    links: {
      type: Array
    },
    defaultArticles: {
      type: Object
    },
    readMoreLabel: {
      type: String
    },
    articlesUrl: {
      type: String
    },
    prevLabel: {
      type: String
    },
    nextLabel: {
      type: String
    },
    imagePlaceholder: {
      type: String
    },
    canShowPublishedDate: {
      type: Boolean
    }
  },

  data() {
    return {
      articles: {},
      activeTag: null,
      currentPage: 1,
      pageCount: {}
    };
  },

  computed: {
    activeArticles() {
      if (!this.activeTag && this.defaultArticles) {
        return this.defaultArticles.articles;
      }

      return this.articles[this.activeTag][this.currentPage];
    },

    activePageCount() {
      if (!this.activeTag && !this.pageCount[this.activeTag]) {
        return 1;
      }

      return this.pageCount[this.activeTag];
    },

    paginationUrl() {
      return `${this.articlesUrl}${this.activeTag}?view=ajax_dynamic_articles`;
    }
  },

  mounted() {
    const { tag, articles } = this.defaultArticles;

    this.articles[tag] = {
      [this.currentPage]: articles
    };

    this.getPaginationData(tag).then(() => {
      this.activeTag = tag;
    });
  },

  methods: {
    getPaginationData(tag) {
      return fetch(this.getPaginatedUrl(tag, 1))
        .then((res) => res.json())
        .then(({ paginate }) => {
          this.currentPage = paginate.currentPage;
          this.pageCount[tag] = paginate.pages;
        });
    },

    getPaginatedUrl(tag, page) {
      if (tag === '*') {
        return `${this.articlesUrl}?view=ajax_dynamic_articles&page=${page}`;
      }

      return `${this.articlesUrl}/tagged/${tag}?view=ajax_dynamic_articles&page=${page}`;
    },

    changeArticles(tag) {
      const url = this.getPaginatedUrl(tag, 1);

      if (!this.articles[tag] || !this.articles[tag][1]) {
        this.fetchArticles(url, tag, 1);
      } else {
        this.activeTag = tag;
        this.currentPage = 1;
      }
    },

    changePage(newPage) {
      const url = this.getPaginatedUrl(this.activeTag, newPage);

      if (
        !this.articles[this.activeTag] ||
        !this.articles[this.activeTag][newPage]
      ) {
        this.fetchArticles(url, this.activeTag, newPage);
      } else {
        this.currentPage = newPage;
      }
    },

    fetchArticles(url, tag, page) {
      return fetch(url)
        .then((res) => res.json())
        .then(({ articles, paginate }) => {
          if (!this.articles[tag]) {
            this.articles[tag] = {};
          }

          this.articles[tag][page] = articles;
          this.activeTag = tag;
          this.currentPage = paginate.currentPage;
          this.pageCount[tag] = paginate.pages;
        })
        .catch((error) => console.error(error));
    },

    showAll() {
      this.changeArticles('*');
    }
  }
};
</script>
